/**
 * 資材（CLAUDE.md §3.4 / §10.8）。
 *
 * 木だけではなく、鉄・布・硝子がある。何が採れるかは「何を壊したか」で決まるので、
 * 「布が足りない」は「布の出る物のところへ猫が行けていない」と同じ意味になる。
 * 数値の在庫画面ではなく、**廃墟のどこがまだ手つかずか**を読ませるための分け方。
 *
 * ここは DOM も Canvas も知らない（§4.4）。
 */
import materialData from '../data/materials.json';

export type MaterialId = 'wood' | 'iron' | 'cloth' | 'glass';

export interface MaterialKind {
  id: MaterialId;
  name: string;
  color: string;
  dark: string;
}

export const MATERIALS: readonly MaterialKind[] = materialData.kinds as MaterialKind[];
export const MATERIAL_IDS: readonly MaterialId[] = MATERIALS.map((m) => m.id);

/** 種類ごとの数。セーブにそのまま載る形（§4.4） */
export type Materials = Record<MaterialId, number>;
/** 一部だけ書いたレシピや取り分 */
export type MaterialBundle = Partial<Record<MaterialId, number>>;

export function emptyMaterials(): Materials {
  return { wood: 0, iron: 0, cloth: 0, glass: 0 };
}

export function materialName(id: MaterialId): string {
  return MATERIALS.find((m) => m.id === id)?.name ?? id;
}

export function addMaterials(into: Materials, add: MaterialBundle): void {
  for (const id of MATERIAL_IDS) into[id] += add[id] ?? 0;
}

export function hasMaterials(have: Materials, need: MaterialBundle): boolean {
  return MATERIAL_IDS.every((id) => have[id] >= (need[id] ?? 0));
}

export function spendMaterials(have: Materials, need: MaterialBundle): void {
  for (const id of MATERIAL_IDS) have[id] -= need[id] ?? 0;
}

/** 足りない分だけを返す。「何が足りないか」を場所ではなく物の名前で言うため */
export function missingMaterials(have: Materials, need: MaterialBundle): MaterialBundle {
  const out: MaterialBundle = {};
  for (const id of MATERIAL_IDS) {
    const short = (need[id] ?? 0) - have[id];
    if (short > 0) out[id] = short;
  }
  return out;
}

export function bundleTotal(bundle: MaterialBundle): number {
  return MATERIAL_IDS.reduce((sum, id) => sum + (bundle[id] ?? 0), 0);
}

/** 「木2 硝子1」のような短い表記。UIの文言をここに集めておく */
export function describeBundle(bundle: MaterialBundle): string {
  return MATERIAL_IDS.filter((id) => (bundle[id] ?? 0) > 0)
    .map((id) => `${materialName(id)}${bundle[id]}`)
    .join(' ');
}
